import type {
    PunchResponse,
    SuccessResponse,
    PunchErrorResponse,
    UserStatusResponse,
    IntermediateResponse,
    ClockRole,
    ClockDepartment
} from '@src/api/punchTypes';
import type { OfflinePunchResult } from '@src/config/offlinePunchService';

export type PunchFlowState =
    | { phase: 'idle' }
    | { phase: 'loading_status' }
    | { phase: 'showing_status'; response: UserStatusResponse }
    | { phase: 'executing_action'; execute: () => Promise<PunchResponse> }
    | { phase: 'showing_result'; result: SuccessResponse | PunchErrorResponse }
    | { phase: 'showing_intermediate'; response: IntermediateResponse }
    | { phase: 'offline_intermediate'; pin: string }
    | { phase: 'offline_role_selection'; pin: string; roles: ClockRole[] }
    | {
          phase: 'offline_department_selection';
          pin: string;
          departments: ClockDepartment[];
      }
    | { phase: 'offline_stored'; punchTime: Date }
    | { phase: 'offline_limit_exceeded' }
    | { phase: 'offline_error'; reason: string }
    | { phase: 'exited' };

export type PunchFlowAction =
    | { type: 'SUBMIT_PIN' }
    | { type: 'STATUS_RECEIVED'; response: UserStatusResponse }
    | { type: 'STATUS_ERROR'; result: PunchErrorResponse }
    | { type: 'ACTION_SELECTED'; execute: () => Promise<PunchResponse> }
    | { type: 'EXECUTION_COMPLETE'; response: PunchResponse }
    | { type: 'INTERMEDIATE_RESOLVED'; execute: () => Promise<PunchResponse> }
    | { type: 'OFFLINE_PIN_ENTERED'; pin: string }
    | { type: 'OFFLINE_RESULT'; pin: string; result: OfflinePunchResult }
    | { type: 'DISMISS' }
    | { type: 'EXIT' };

export function punchFlowReducer(
    state: PunchFlowState,
    action: PunchFlowAction
): PunchFlowState {
    switch (action.type) {
        case 'SUBMIT_PIN':
            return { phase: 'loading_status' };

        case 'STATUS_RECEIVED':
            return { phase: 'showing_status', response: action.response };

        case 'STATUS_ERROR':
            return { phase: 'showing_result', result: action.result };

        case 'ACTION_SELECTED':
            return { phase: 'executing_action', execute: action.execute };

        case 'EXECUTION_COMPLETE': {
            const r = action.response;
            if (r.type === 'success' || r.type === 'error') {
                return { phase: 'showing_result', result: r };
            }
            if (r.type === 'user_status') {
                return { phase: 'showing_status', response: r };
            }
            return { phase: 'showing_intermediate', response: r };
        }

        case 'INTERMEDIATE_RESOLVED':
            return { phase: 'executing_action', execute: action.execute };

        case 'OFFLINE_PIN_ENTERED':
            return { phase: 'offline_intermediate', pin: action.pin };

        case 'OFFLINE_RESULT': {
            const { pin, result } = action;
            switch (result.type) {
                case 'stored':
                    return { phase: 'offline_stored', punchTime: result.punchTime };
                case 'needsRoleSelection':
                    return {
                        phase: 'offline_role_selection',
                        pin,
                        roles: result.roles
                    };
                case 'needsDepartmentSelection':
                    return {
                        phase: 'offline_department_selection',
                        pin,
                        departments: result.departments
                    };
                case 'limitExceeded':
                    return { phase: 'offline_limit_exceeded' };
                case 'error':
                    return { phase: 'offline_error', reason: result.reason };
                default:
                    return state;
            }
        }

        case 'DISMISS':
            return { phase: 'idle' };

        case 'EXIT':
            return { phase: 'exited' };

        default:
            return state;
    }
}
